/**
 * Connectivity tab (#252) — per-modem view of a managed (persistent-attach /
 * PLMN-hunt) modem: live state, uptime / attach KPIs from rollups, probe RTT
 * chart, the event timeline (attach / detach / failure with cause, scans
 * incl. negatives), pass-window overlay for hunt mode, modem-scoped capture
 * artifacts with "Grab last N min" / "Scan now".
 *
 * Reads: `connectivityStatus` live via the parent's modem list; series via
 * the api function's /devices/:id/connectivity/* routes (same API the
 * customer's own systems use); artifacts via onSnapshot.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { CentralEdgeModem } from '@simcheck/shared-types';
import { Badge, Button, Card, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui';
import { ResponsiveTable } from '@/components/ui/responsive-table';
import type { Column } from '@/components/ui/data-table';
import { LineChart } from '@/components/charts/LineChart';
import { ExecutionArtifacts } from '@/components/artifacts/ExecutionArtifacts';
import { useModemArtifacts } from '@/hooks/useModemArtifacts';
import { useDateFormat } from '@/hooks/useDateFormat';
import { useToast } from '@/contexts/ToastContext';
import {
  getConnectivityEvents, getConnectivityRollups, getScanResults, getModemArtifactSignedUrl, grabCapture, huntScanNow,
  listPassWindows, type ConnectivityEventRow, type RollupRow, type ScanResultRow, type PassWindowRow,
} from '@/services/connectivityService';
import { Activity, Radar, Download, RefreshCw } from 'lucide-react';

type Period = '24h' | '7d' | '30d' | '8w';
const PERIOD_MS: Record<Period, number> = { '24h': 86400_000, '7d': 7 * 86400_000, '30d': 30 * 86400_000, '8w': 56 * 86400_000 };

interface Props {
  deviceId: string;
  modems: CentralEdgeModem[];
  /** Re-read the device's modems (live state chip). The tab's own load() only covers history. */
  onRefreshStatus?: () => void;
}

const pct = (v: number | null | undefined) => (v == null ? '—' : `${v}%`);
const ms = (v: number | null | undefined) => (v == null ? '—' : `${v} ms`);

export const ConnectivityTab: React.FC<Props> = ({ deviceId, modems, onRefreshStatus }) => {
  const fmt = useDateFormat();
  const { success, error: showError } = useToast();
  const managed = useMemo(() => modems.filter((m) => m.connectivityMode), [modems]);
  const [imei, setImei] = useState<string>('');
  const [period, setPeriod] = useState<Period>('24h');
  const [rollups, setRollups] = useState<RollupRow[]>([]);
  const [granularity, setGranularity] = useState<'hour' | 'day'>('hour');
  const [events, setEvents] = useState<ConnectivityEventRow[]>([]);
  const [scans, setScans] = useState<ScanResultRow[]>([]);
  const [windows, setWindows] = useState<PassWindowRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [grabMinutes, setGrabMinutes] = useState('5');

  useEffect(() => {
    if (!imei && managed.length > 0) setImei(managed[0].imei || managed[0].modemId || '');
  }, [managed, imei]);

  const modem = managed.find((m) => (m.imei || m.modemId) === imei) || null;
  const status = modem?.connectivityStatus;
  const isHunt = modem?.connectivityMode === 'hunt';
  const artifacts = useModemArtifacts(deviceId, imei);

  const load = useCallback(async () => {
    if (!imei) return;
    setLoading(true);
    const to = new Date();
    const from = new Date(to.getTime() - PERIOD_MS[period]);
    try {
      // One failing panel must not blank the others (an events 500 used to wipe the KPIs too).
      const [r, e, s, w] = await Promise.allSettled([
        getConnectivityRollups(deviceId, { from, to, modemImei: imei }),
        getConnectivityEvents(deviceId, { from, to, modemImei: imei, limit: 1000, order: 'desc' }),
        isHunt ? getScanResults(deviceId, { from, to, modemImei: imei, limit: 1000, order: 'desc' }) : Promise.resolve({ items: [] as ScanResultRow[], nextCursor: null }),
        isHunt ? listPassWindows(deviceId, { from, to: new Date(to.getTime() + 2 * 86400_000), limit: 500 }) : Promise.resolve([] as PassWindowRow[]),
      ]);
      if (r.status === 'fulfilled') { setRollups(r.value.items); setGranularity(r.value.granularity); }
      // order=desc → already newest first (and the NEWEST 1000, not the oldest).
      if (e.status === 'fulfilled') setEvents(e.value.items);
      if (s.status === 'fulfilled') setScans(s.value.items);
      if (w.status === 'fulfilled') setWindows(w.value);
      const failed = [r, e, s, w].find((x) => x.status === 'rejected') as PromiseRejectedResult | undefined;
      if (failed) showError(failed.reason?.message || 'Some connectivity data failed to load');
    } catch (err: any) {
      showError(err?.message || 'Failed to load connectivity data');
    } finally {
      setLoading(false);
    }
  }, [deviceId, imei, period, isHunt, showError]);

  useEffect(() => { load(); const t = setInterval(load, 30_000); return () => clearInterval(t); }, [load]);

  // ── KPI totals over the period ───────────────────────────────────────────
  const totals = useMemo(() => {
    const sum = (k: keyof RollupRow) => rollups.reduce((a, r) => a + (Number(r[k]) || 0), 0);
    const bucketSec = granularity === 'day' ? 86400 : 3600;
    // Clamp per bucket: a bucket can't hold more attached time than its own length.
    const attachedSec = rollups.reduce((a, r) => a + Math.min(Number(r.attachedSec) || 0, bucketSec), 0);
    const elapsedSec = rollups.length * bucketSec;
    const attempts = sum('attempts'); const attaches = sum('attaches'); const scansN = sum('scans'); const det = sum('targetDetections');
    const probes = sum('probes'); const fail = sum('probeFail');
    const causes: Record<string, number> = {};
    for (const r of rollups) for (const [c, n] of Object.entries(r.failuresByCause || {})) causes[c] = (causes[c] || 0) + n;
    return {
      uptimePct: elapsedSec > 0 ? Math.round((attachedSec / elapsedSec) * 1000) / 10 : null,
      attempts, attaches, failures: sum('failures'), detaches: sum('detaches'),
      attachSuccessRate: attempts > 0 ? Math.round((attaches / attempts) * 1000) / 10 : null,
      scans: scansN, detections: det, detectionRate: scansN > 0 ? Math.round((det / scansN) * 1000) / 10 : null,
      probeLossPct: probes > 0 ? Math.round((fail / probes) * 1000) / 10 : null,
      causes: Object.entries(causes).sort((a, b) => b[1] - a[1]),
    };
  }, [rollups, granularity]);

  const chartData = useMemo(() => rollups.map((r) => ({
    t: granularity === 'day' ? fmt.formatDate(r.bucketStart) : fmt.formatTime(r.bucketStart),
    p50: r.derived.rttP50Ms, p95: r.derived.rttP95Ms, loss: r.derived.probeLossPct, uptime: r.derived.uptimePct,
  })), [rollups, granularity, fmt]);

  const detectionData = useMemo(() => scans.slice().reverse().filter((s) => s.targetSeen).map((s) => {  // chart runs oldest → newest
    const best = s.cells.filter((c) => c.target && c.rsrp != null).sort((a, b) => (b.rsrp ?? -999) - (a.rsrp ?? -999))[0];
    return { t: fmt.formatTime(s.ts), rsrp: best?.rsrp ?? null, normalized: best?.rsrpNormalized ?? null };
  }), [scans, fmt]);

  const eventColumns: Column<ConnectivityEventRow>[] = [
    { key: 'ts', header: 'Time', width: '18%', render: (v) => <span className="font-mono text-xs">{fmt.formatDateTime(String(v))}</span> },
    { key: 'kind', header: 'Event', width: '16%', render: (v) => <Badge variant={String(v).includes('fail') || String(v) === 'detach' ? 'destructive' : String(v).includes('ok') || String(v) === 'bearer_up' ? 'default' : 'secondary'} className="text-[10px]">{String(v).replace(/_/g, ' ')}</Badge> },
    { key: 'plmn', header: 'PLMN / RAT', width: '14%', render: (_v, r) => <span className="text-xs">{[r.plmn, r.rat, r.band].filter(Boolean).join(' · ') || '—'}</span> },
    { key: 'cause', header: 'Cause', width: '22%', render: (_v, r) => <span className="text-xs">{r.cause ? `${r.cause.code ?? ''} ${r.cause.text ?? ''}`.trim() : (r.detail ? String(r.detail) : '—')}</span> },
    { key: 'windowId', header: 'Window', width: '12%', render: (v) => <span className="text-xs">{v ? String(v).slice(0, 8) : '—'}</span> },
    { key: 'attachTimeMs', header: 'Attach / RSRP', width: '18%', render: (_v, r) => <span className="text-xs">{r.attachTimeMs != null ? `${r.attachTimeMs} ms` : ''}{r.rsrp != null ? ` ${r.rsrp} dBm` : ''}{r.rsrpNormalized != null ? ` (${r.rsrpNormalized} norm.)` : ''}</span> },
  ];
  const scanColumns: Column<ScanResultRow>[] = [
    { key: 'ts', header: 'Time', width: '20%', render: (v) => <span className="font-mono text-xs">{fmt.formatDateTime(String(v))}</span> },
    { key: 'targetSeen', header: 'Target', width: '12%', render: (v) => <Badge variant={v ? 'default' : 'secondary'} className="text-[10px]">{v ? 'seen' : 'not seen'}</Badge> },
    { key: 'durationMs', header: 'Duration', width: '12%', render: (v) => <span className="text-xs">{v != null ? `${Math.round(Number(v) / 100) / 10} s` : '—'}</span> },
    { key: 'cells', header: 'Cells (PLMN · band · RSRP raw / norm.)', width: '44%', render: (_v, r) => <span className="text-xs">{r.cells.length === 0 ? 'none' : r.cells.slice(0, 6).map((c) => `${c.plmn ?? '?'}${c.band ? ` B${c.band}` : ''} ${c.rsrp ?? '?'}${c.rsrpNormalized != null ? `/${c.rsrpNormalized}` : ''}`).join(', ')}{r.cells.length > 6 ? ` +${r.cells.length - 6}` : ''}</span> },
    { key: 'windowId', header: 'Window', width: '12%', render: (v) => <span className="text-xs">{v ? String(v).slice(0, 8) : '—'}</span> },
  ];

  const handleGrab = async () => {
    try { const r = await grabCapture(deviceId, imei, Number(grabMinutes) || 5); success(`Capture grab requested (${r.minutes} min) — the artifact appears below when uploaded`); }
    catch (err: any) { showError(err?.message || 'Grab failed'); }
  };
  const handleScanNow = async () => {
    try { await huntScanNow(deviceId, imei, false); success('Scan requested'); }
    catch (err: any) { showError(err?.message || 'Scan failed'); }
  };

  if (managed.length === 0) {
    return (
      <Card padding="lg">
        <p className="text-sm text-muted-foreground">No modem on this device runs a connectivity mode. Enable <strong>Persistent</strong> or <strong>PLMN hunt</strong> in the Modems tab.</p>
      </Card>
    );
  }

  const stateTone = status?.state === 'attached' || status?.state === 'hunt_attached' || status?.state === 'hunt_terrestrial' ? 'default'
    : status?.state === 'down' || status?.state === 'anchor_mismatch' || status?.state === 'modem_lost' ? 'destructive' : 'secondary';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={imei} onValueChange={setImei}>
          <SelectTrigger className="w-72"><SelectValue placeholder="Modem" /></SelectTrigger>
          <SelectContent>
            {managed.map((m) => <SelectItem key={m.imei || m.modemId} value={m.imei || m.modemId || ''}>{m.modemModelName || m.model} · …{(m.imei || '').slice(-6)} · {m.connectivityMode === 'hunt' ? 'PLMN hunt' : 'persistent'}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={period} onValueChange={(v) => setPeriod(v as Period)}>
          <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="24h">24 hours</SelectItem><SelectItem value="7d">7 days</SelectItem><SelectItem value="30d">30 days</SelectItem><SelectItem value="8w">8 weeks</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => { onRefreshStatus?.(); load(); }} disabled={loading}><RefreshCw className="mr-1 h-3.5 w-3.5" />Refresh</Button>
        {status && (
          <Badge variant={stateTone as any}><Activity className="mr-1 h-3 w-3" />{String(status.state).replace(/_/g, ' ')}{status.since ? ` · since ${fmt.formatDateTime(status.since as any)}` : ''}</Badge>
        )}
        {status?.plmn && <Badge variant="outline">{[status.plmn, status.rat, status.band].filter(Boolean).join(' · ')}</Badge>}
        {status?.position && <Badge variant="outline">{status.position.lat.toFixed(5)}, {status.position.lon.toFixed(5)}</Badge>}
        {status?.moduleTempC != null && <Badge variant="outline">{status.moduleTempC} °C</Badge>}
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-6">
        {[
          ['Uptime', pct(totals.uptimePct)], ['Attach attempts', String(totals.attempts)], ['Attach success', pct(totals.attachSuccessRate)],
          ['Detaches', String(totals.detaches)], isHunt ? ['Scans', String(totals.scans)] : ['Probe loss', pct(totals.probeLossPct)],
          isHunt ? ['Target seen', `${totals.detections} (${pct(totals.detectionRate)})`] : ['RTT p95 (last)', ms(rollups[rollups.length - 1]?.derived.rttP95Ms)],
        ].map(([label, value]) => (
          <Card key={label} padding="md"><div className="text-[11px] uppercase text-muted-foreground">{label}</div><div className="text-lg font-semibold">{value}</div></Card>
        ))}
      </div>

      {chartData.length > 1 && (
        <Card padding="lg">
          <div className="text-caption uppercase text-muted-foreground mb-2">Probe RTT (p50 / p95) and loss per {granularity}</div>
          <LineChart data={chartData} xAxisKey="t" height={220} showDots lines={[{ dataKey: 'p50', name: 'RTT p50 (ms)', color: 'hsl(var(--chart-1))' }, { dataKey: 'p95', name: 'RTT p95 (ms)', color: 'hsl(var(--chart-2))' }, { dataKey: 'loss', name: 'Loss (%)', color: 'hsl(var(--chart-5))' }, { dataKey: 'uptime', name: 'Uptime (%)', color: 'hsl(var(--chart-4))' }]} />
        </Card>
      )}

      {isHunt && detectionData.length > 1 && (
        <Card padding="lg">
          <div className="text-caption uppercase text-muted-foreground mb-2">Target RSRP — raw vs normalised (SC-5)</div>
          <LineChart data={detectionData} xAxisKey="t" height={200} showDots lines={[{ dataKey: 'rsrp', name: 'RSRP raw (dBm)', color: 'hsl(var(--chart-1))' }, { dataKey: 'normalized', name: 'RSRP normalised (dBm)', color: 'hsl(var(--chart-2))' }]} />
        </Card>
      )}

      {totals.causes.length > 0 && (
        <Card padding="lg">
          <div className="text-caption uppercase text-muted-foreground mb-2">Attach failures by cause</div>
          <div className="flex flex-wrap gap-2">{totals.causes.map(([c, n]) => <Badge key={c} variant="outline">{c}: {n}</Badge>)}</div>
        </Card>
      )}

      {isHunt && windows.length > 0 && (
        <Card padding="lg">
          <div className="text-caption uppercase text-muted-foreground mb-2">Pass windows ({windows.length})</div>
          <div className="flex flex-wrap gap-2">
            {windows.map((w) => {
              const inWin = events.filter((e) => e.windowId === w.id);
              const attempts = inWin.filter((e) => e.kind === 'attach_attempt').length;
              const ok = inWin.filter((e) => e.kind === 'attach_ok').length;
              return <Badge key={w.id} variant={ok > 0 ? 'default' : attempts > 0 ? 'destructive' : 'outline'} className="text-[10px]">{fmt.formatDateTime(w.start)} → {fmt.formatTime(w.end)}{w.satelliteId ? ` ${w.satelliteId}` : ''} · {attempts} att / {ok} ok</Badge>;
            })}
          </div>
        </Card>
      )}

      {isHunt && (
        <Card padding="lg">
          <div className="flex items-center justify-between mb-2">
            <div className="text-caption uppercase text-muted-foreground">Scans ({scans.length}) — negatives included</div>
            <Button variant="outline" size="sm" onClick={handleScanNow}><Radar className="mr-1 h-3.5 w-3.5" />Scan now</Button>
          </div>
          <ResponsiveTable<ScanResultRow> data={scans} columns={scanColumns} keyExtractor={(r) => r.id} paginated defaultPageSize={25} />
        </Card>
      )}

      <Card padding="lg">
        <div className="text-caption uppercase text-muted-foreground mb-2">Events ({events.length})</div>
        <ResponsiveTable<ConnectivityEventRow> data={events} columns={eventColumns} keyExtractor={(r) => r.id} paginated defaultPageSize={25} />
      </Card>

      <Card padding="lg">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div className="text-caption uppercase text-muted-foreground">Captures ({artifacts.length})</div>
          <div className="flex items-center gap-2">
            <Select value={grabMinutes} onValueChange={setGrabMinutes}>
              <SelectTrigger className="h-8 w-28 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{['5', '15', '30', '60'].map((m) => <SelectItem key={m} value={m}>last {m} min</SelectItem>)}</SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={handleGrab}><Download className="mr-1 h-3.5 w-3.5" />Grab capture</Button>
          </div>
        </div>
        {artifacts.length === 0 ? (
          <p className="text-xs text-muted-foreground">No capture windows yet. Event-triggered windows appear here after a detach, attach attempt or probe failure; use Grab for an ad-hoc window.</p>
        ) : (
          <ExecutionArtifacts
            artifacts={artifacts}
            executionId=""
            organizationId={null}
            gridClassName="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
            signedUrlFetcher={(id) => getModemArtifactSignedUrl(deviceId, imei, id)}
            extraSubtitle={(a: any) => [a.trigger, a.windowStart ? fmt.formatDateTime(a.windowStart?.toDate ? a.windowStart.toDate() : a.windowStart) : null].filter(Boolean).join(' · ') || null}
          />
        )}
      </Card>
    </div>
  );
};
